import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Building2, Plus, Users, Stethoscope, Activity } from 'lucide-react';
import { toast } from 'sonner';

export default function TenantsPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'clinic', subdomain: '' });

  const fetchTenants = async () => {
    try {
      const res = await API.get('/tenants');
      setTenants(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTenants(); }, []);

  const handleCreate = async () => {
    try {
      await API.post('/tenants', form);
      toast.success('Tenant created');
      setDialogOpen(false);
      setForm({ name: '', type: 'clinic', subdomain: '' });
      fetchTenants();
    } catch (err) { toast.error('Failed to create tenant'); }
  };

  const toggleActive = async (tenant) => {
    try {
      await API.put(`/tenants/${tenant.id}`, { is_active: !tenant.is_active });
      fetchTenants();
      toast.success(`Tenant ${tenant.is_active ? 'deactivated' : 'activated'}`);
    } catch (err) { toast.error('Failed to update tenant'); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Activity className="w-8 h-8 text-sky-600 animate-pulse-subtle" /></div>;

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-[1400px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 data-testid="tenants-title" className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Tenants</h1>
          <p className="text-slate-500 mt-1">{tenants.length} organizations registered</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-tenant-btn" className="bg-sky-600 hover:bg-sky-700">
              <Plus className="w-4 h-4 mr-2" /> Add Tenant
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{fontFamily: 'Manrope'}}>Create New Tenant</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Organization Name</Label>
                <Input data-testid="tenant-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="City Health Clinic" />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                  <SelectTrigger data-testid="tenant-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clinic">Clinic</SelectItem>
                    <SelectItem value="hospital">Hospital</SelectItem>
                    <SelectItem value="medical_group">Medical Group</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Subdomain</Label>
                <Input data-testid="tenant-subdomain" value={form.subdomain} onChange={e => setForm({...form, subdomain: e.target.value})} placeholder="cityhealth" />
              </div>
              <Button data-testid="tenant-submit" onClick={handleCreate} className="w-full bg-sky-600 hover:bg-sky-700">Create Tenant</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-700">Organization</TableHead>
                <TableHead className="font-semibold text-slate-700">Type</TableHead>
                <TableHead className="font-semibold text-slate-700">Subdomain</TableHead>
                <TableHead className="font-semibold text-slate-700">Users</TableHead>
                <TableHead className="font-semibold text-slate-700">Patients</TableHead>
                <TableHead className="font-semibold text-slate-700">Status</TableHead>
                <TableHead className="font-semibold text-slate-700">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                    <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No tenants found</p>
                  </TableCell>
                </TableRow>
              ) : tenants.map((tenant) => (
                <TableRow key={tenant.id} data-testid={`tenant-row-${tenant.id}`} className="hover:bg-slate-50">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center">
                        {tenant.type === 'hospital' ? <Building2 className="w-4 h-4 text-sky-600" /> : <Stethoscope className="w-4 h-4 text-sky-600" />}
                      </div>
                      <span className="font-medium text-slate-900">{tenant.name}</span>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="secondary" className="capitalize">{tenant.type?.replace(/_/g, ' ')}</Badge></TableCell>
                  <TableCell className="text-slate-600">{tenant.subdomain || '-'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-sm">{tenant.user_count || 0}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{tenant.patient_count || 0}</TableCell>
                  <TableCell>
                    <Badge className={tenant.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                      {tenant.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      data-testid={`toggle-tenant-${tenant.id}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(tenant)}
                      className={tenant.is_active ? 'text-red-600 hover:text-red-700' : 'text-emerald-600 hover:text-emerald-700'}
                    >
                      {tenant.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
